import { action, observable } from 'mobx';

interface IStorageOperator {
  get: () => string | null;
  set: (v: string) => void;
  setIfAbsent: (v: string) => boolean;
  clear: () => void;
}

enum StoreType {
  ST_SESSION_STORAGE,
  ST_LOCAL_STORAGE,
}

class BuiltInStorageOperator implements IStorageOperator {
  private storage: Storage;
  private key: string;

  constructor(type: StoreType, key: string) {
    switch (type) {
      case StoreType.ST_SESSION_STORAGE:
        this.storage = sessionStorage;
        break;
      case StoreType.ST_LOCAL_STORAGE:
        this.storage = localStorage;
        break;
    }
    this.key = key;
  }

  public get(): string | null {
    return this.storage.getItem(this.key);
  }

  public set(v: string): void {
    this.storage.setItem(this.key, v);
  }

  public setIfAbsent(v: string): boolean {
    if (this.get() === null) {
      this.set(v);
      return true;
    } else {
      return false;
    }
  }

  public clear(): void {
    this.storage.removeItem(this.key);
  }
}

class StoreBase {
  protected key: string;
  protected type: StoreType;
  private op: IStorageOperator;

  constructor(type: StoreType, key: string) {
    this.key = key;
    this.type = type;
    switch (type) {
      case StoreType.ST_SESSION_STORAGE:
      case StoreType.ST_LOCAL_STORAGE:
        this.op = new BuiltInStorageOperator(type, key);
        break;
      default:
        throw Error(`invalid store type ${type} for key ${key}`);
    }
  }

  public getStr(): string | null {
    return this.op.get();
  }

  public setStr(v: string): void {
    this.op.set(v);
  }

  public setStrIfAbsent(v: string): boolean {
    if (this.getStr() === null) {
      this.setStr(v);
      return true;
    } else {
      return false;
    }
  }

  public clear(): void {
    this.op.clear();
  }
}

export class Store<T> extends StoreBase {
  public get(): Readonly<T> | null {
    return this.getInner();
  }

  public getNotNull(): Readonly<T> {
    const v = this.get();
    if (v === null) throw Error(`getNotNull with ${this.key} encountered null value`);
    return v;
  }

  public set(v: T): void {
    const vStr = JSON.stringify(v);
    this.setStr(vStr);
  }

  public setIfAbsent(v: T): boolean {
    const vStr = JSON.stringify(v);
    return this.setStrIfAbsent(vStr);
  }

  protected getInner(): T | null {
    const vStr = this.getStr();
    if (vStr === null) return null;
    try {
      return JSON.parse(vStr) as T;
    } catch (e) {
      super.clear();
      return null;
    }
  }
}

type TMemberSettingCallback<T> = (v: T) => void;
type TDefaultValueGenerator<T> = () => T | null;

export class StoreWithCache<T> extends Store<T> {
  protected value: { v: T | null };
  protected defaultValueGenerator: TDefaultValueGenerator<T>;

  constructor(type: StoreType, key: string) {
    super(type, key);
    this.defaultValueGenerator = () => null;
    this.value = { v: null };
    this.value.v = super.getInner();
  }

  public setDefault(defaultValueGenerator: TDefaultValueGenerator<T>) {
    this.defaultValueGenerator = defaultValueGenerator;
  }

  public get(): Readonly<T> | null {
    const v = this.value.v;
    if (v) return v;
    return this.defaultValueGenerator();
  }

  public set(v: T): void {
    this.setCacheFinal(JSON.parse(JSON.stringify(v)));
    super.set(v);
  }

  public setIfAbsent(v: T): boolean {
    // Check if parsable
    const vToSet = JSON.parse(JSON.stringify(v));
    if (super.setIfAbsent(vToSet)) {
      this.setCacheFinal(vToSet);
      return true;
    } else {
      return false;
    }
  }

  public setMember(cb: TMemberSettingCallback<T>) {
    const vToSet = this.value.v || JSON.parse(JSON.stringify(this.defaultValueGenerator()));
    if (vToSet) {
      this.setMemberFinal(vToSet, cb);
      if (vToSet === this.value.v) {
        super.set(vToSet);
      } else {
        this.set(vToSet);
      }
    }
  }

  public clear(): void {
    this.setCacheFinal(null);
    super.clear();
  }

  protected setCacheFinal(v: T | null): void {
    this.value.v = v;
  }

  protected setMemberFinal(v: T, cb: TMemberSettingCallback<T>) {
    cb(v);
  }
}

export class StoreObservable<T> extends StoreWithCache<T> {
  @observable
  private proxiedValue: { v: T | null };

  constructor(type: StoreType, key: string) {
    super(type, key);
    this.proxiedValue = this.value;
    this.value = this.proxiedValue;
  }

  @action
  protected setCacheFinal(v: T | null): void {
    super.setCacheFinal(v);
  }

  @action
  protected setMemberFinal(v: T, cb: TMemberSettingCallback<T>) {
    super.setMemberFinal(v, cb);
  }
}

class StoreManager {
  private type: StoreType;
  private stores: { [key: string]: StoreBase };

  constructor(type: StoreType) {
    this.type = type;
    this.stores = {};
  }

  public getStore<T>(key: string): Store<T> {
    const store = this._getStore<T>(key);
    if (store === null) return this.createStore<T>(key);
    else return store;
  }

  public getStoreWithCache<T>(key: string): StoreWithCache<T> {
    const store = this._getStore<T>(key);
    if (store === null) return this.createStoreWithCache<T>(key);
    if (!(store instanceof StoreWithCache)) {
      throw Error(`SessionStore ${key} type doesn't match`);
    }
    return store as StoreWithCache<T>;
  }

  public getStoreObservable<T>(key: string): StoreObservable<T> {
    const store = this._getStore<T>(key);
    if (store === null) return this.createStoreObservable<T>(key);
    if (!(store instanceof StoreObservable)) {
      throw Error(`SessionStore ${key} type doesn't match`);
    }
    return store as StoreObservable<T>;
  }

  private _getStore<T>(key: string): Store<T> | null {
    if (!(key in this.stores)) return null;
    return this.stores[key] as Store<T>;
  }

  private createStore<T>(key: string): Store<T> {
    const store = new Store<T>(this.type, key);
    this.stores[key] = store;
    return store;
  }

  private createStoreWithCache<T>(key: string): StoreWithCache<T> {
    const store = new StoreWithCache<T>(this.type, key);
    this.stores[key] = store;
    return store;
  }

  private createStoreObservable<T>(key: string): StoreObservable<T> {
    const store = new StoreObservable<T>(this.type, key);
    this.stores[key] = store;
    return store;
  }
}

export const lsm = new StoreManager(StoreType.ST_LOCAL_STORAGE);
export const ssm = new StoreManager(StoreType.ST_SESSION_STORAGE);
